import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import dayjs from "dayjs";
import type { CoReviewComment } from "../../shared/interfaces/CoReviewComment";
import {
  formatCommentAuthor,
  groupCommentsByThread,
} from "@/features/co-review/api/coReviewCommentAdapter";
import { CommentReplyComposer } from "./CommentReplyComposer";

export type CommentRailMode = "co-edit" | "officer" | "officer-readonly";

interface SectionCommentRailProps {
  sectionPath: string;
  fieldLabel?: string;
  comments: CoReviewComment[];
  mode: CommentRailMode;
  /** The report's owning officer; used to tag each comment as CO vs Officer. */
  reportOwnerUserId?: string;
  onAdd?: (commentText: string) => void;
  onResolve?: (commentId: string) => void;
  onUnresolve?: (commentId: string) => void;
  onDeletePending?: (commentId: string) => void;
  onFixWithAi?: (commentId: string) => void | Promise<void>;
  onReply?: (parentCommentId: string, text: string) => void | Promise<void>;
}

export function SectionCommentRail({
  sectionPath,
  fieldLabel,
  comments,
  mode,
  reportOwnerUserId,
  onAdd,
  onResolve,
  onUnresolve,
  onDeletePending,
  onFixWithAi,
  onReply,
}: SectionCommentRailProps) {
  const theme = useTheme();
  const [draft, setDraft] = useState("");
  const [adding, setAdding] = useState(false);
  const [fixingId, setFixingId] = useState<string | null>(null);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  // Per-thread collapse override. Absent → default (collapsed iff resolved).
  // Present → explicit user choice, which sticks across resolve/unresolve until
  // they toggle again.
  const [collapseOverride, setCollapseOverride] = useState<
    Record<string, boolean>
  >({});
  const replyEnabled = onReply && (mode === "officer" || mode === "co-edit");
  const threads = groupCommentsByThread(comments);

  const isCollapsed = (parentId: string, resolved: boolean): boolean =>
    collapseOverride[parentId] ?? resolved;

  const toggleCollapsed = (parentId: string, resolved: boolean) => {
    const current = isCollapsed(parentId, resolved);
    setCollapseOverride((prev) => ({ ...prev, [parentId]: !current }));
    // Closing a thread also closes any open reply composer on it.
    if (!current && replyingTo === parentId) setReplyingTo(null);
  };

  // Resolving/unresolving snaps the thread back to its default visibility
  // (collapsed when resolved, expanded when not) — even if the user had a
  // manual override in effect.
  const clearOverride = (parentId: string) => {
    setCollapseOverride((prev) => {
      if (!(parentId in prev)) return prev;
      const next = { ...prev };
      delete next[parentId];
      return next;
    });
  };

  const handleResolveClick = (parentId: string) => {
    clearOverride(parentId);
    if (replyingTo === parentId) setReplyingTo(null);
    onResolve?.(parentId);
  };

  const handleUnresolveClick = (parentId: string) => {
    clearOverride(parentId);
    onUnresolve?.(parentId);
  };

  const handleFixClick = async (commentId: string) => {
    if (!onFixWithAi) return;
    setFixingId(commentId);
    try {
      await onFixWithAi(commentId);
    } finally {
      setFixingId(null);
    }
  };

  // Replies aren't counted as standalone comments — header tallies reflect
  // only top-level threads.
  const topLevel = comments.filter((c) => !c.parentCommentId);
  const unresolved = topLevel.filter((c) => !c.resolvedAt);
  const showHeader = comments.length > 0 || mode === "co-edit";

  if (!showHeader) return null;

  const accentColor =
    unresolved.length > 0
      ? theme.palette.warning.main
      : theme.palette.success.main;

  const submit = () => {
    if (!onAdd || !draft.trim()) return;
    onAdd(draft.trim());
    setDraft("");
    setAdding(false);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 1,
        ml: { xs: 0, md: 0 },
        p: 1.5,
        borderRadius: 1.5,
        border: `1px solid ${alpha(accentColor, 0.4)}`,
        borderLeft: `4px solid ${accentColor}`,
        bgcolor: alpha(accentColor, 0.04),
      }}
      data-co-section-path={sectionPath}
    >
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <AddCommentOutlinedIcon
          sx={{ color: accentColor, fontSize: "1rem" }}
        />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            letterSpacing: 0.4,
            textTransform: "uppercase",
            color: accentColor,
          }}
        >
          CO Review · {topLevel.length} comment
          {topLevel.length === 1 ? "" : "s"}
          {unresolved.length > 0 ? ` · ${unresolved.length} open` : ""}
        </Typography>
        {fieldLabel && (
          <Typography
            variant="caption"
            sx={{ ml: "auto", color: "text.secondary", fontSize: "0.7rem" }}
            noWrap
          >
            {fieldLabel}
          </Typography>
        )}
      </Box>

      <Stack spacing={1}>
        {threads.map(({ parent, replies }) => {
          const resolved = !!parent.resolvedAt;
          const collapsed = isCollapsed(parent.commentId, resolved);
          const isReplying = replyingTo === parent.commentId;
          return (
            <Box key={parent.commentId}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1,
                  bgcolor: "background.paper",
                  opacity: resolved ? 0.65 : 1,
                  border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={collapsed ? 0 : 0.5}>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, fontSize: "0.7rem" }}
                  >
                    {formatCommentAuthor(parent, reportOwnerUserId)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.65rem" }}
                  >
                    {dayjs(parent.createdAt).format("MMM D, h:mm A")} · cycle{" "}
                    {parent.reviewCycle}
                  </Typography>
                  {collapsed && replies.length > 0 && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.65rem" }}
                    >
                      · {replies.length}{" "}
                      {replies.length === 1 ? "reply" : "replies"}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      ml: "auto",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.25,
                    }}
                  >
                    {!collapsed &&
                      mode === "officer" &&
                      !resolved &&
                      !parent.isSectionRejection &&
                      onFixWithAi && (
                        <Tooltip title="Implement suggestion with AI">
                          <span>
                            <IconButton
                              size="small"
                              disabled={fixingId === parent.commentId}
                              onClick={() => handleFixClick(parent.commentId)}
                            >
                              {fixingId === parent.commentId ? (
                                <CircularProgress size={14} />
                              ) : (
                                <AutoAwesomeIcon
                                  fontSize="small"
                                  sx={{ color: theme.palette.primary.main }}
                                />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    {!collapsed && replyEnabled && (
                      <Tooltip title="Reply">
                        <IconButton
                          size="small"
                          onClick={() =>
                            setReplyingTo(isReplying ? null : parent.commentId)
                          }
                        >
                          <ReplyOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {(mode === "officer" || mode === "co-edit") && (
                      <Tooltip
                        title={resolved ? "Mark unresolved" : "Mark resolved"}
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            resolved
                              ? handleUnresolveClick(parent.commentId)
                              : handleResolveClick(parent.commentId)
                          }
                        >
                          {resolved ? (
                            <CheckCircleIcon
                              fontSize="small"
                              sx={{ color: theme.palette.success.main }}
                            />
                          ) : (
                            <RadioButtonUncheckedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                    {!collapsed && mode === "co-edit" && onDeletePending && (
                      <Tooltip title="Remove comment">
                        <IconButton
                          size="small"
                          onClick={() => onDeletePending(parent.commentId)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={collapsed ? "Expand" : "Collapse"}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          toggleCollapsed(parent.commentId, resolved)
                        }
                      >
                        {collapsed ? (
                          <ExpandMoreIcon fontSize="small" />
                        ) : (
                          <ExpandLessIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                {!collapsed && (
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {parent.commentText}
                  </Typography>
                )}
              </Box>

              {!collapsed && (replies.length > 0 || isReplying) && (
                <Stack
                  spacing={0.75}
                  sx={{
                    mt: 0.5,
                    pl: 2,
                    borderLeft: `2px solid ${alpha(theme.palette.divider, 0.7)}`,
                  }}
                >
                  {replies.map((r) => (
                    <Box
                      key={r.commentId}
                      sx={{
                        p: 0.75,
                        borderRadius: 1,
                        bgcolor: "background.paper",
                        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1} mb={0.25}>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, fontSize: "0.7rem" }}
                        >
                          {formatCommentAuthor(r, reportOwnerUserId)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.65rem" }}
                        >
                          {dayjs(r.createdAt).format("MMM D, h:mm A")}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {r.commentText}
                      </Typography>
                    </Box>
                  ))}
                  {isReplying && onReply && (
                    <CommentReplyComposer
                      onSubmit={async (text) => {
                        await onReply(parent.commentId, text);
                        setReplyingTo(null);
                      }}
                      onCancel={() => setReplyingTo(null)}
                    />
                  )}
                </Stack>
              )}
            </Box>
          );
        })}
      </Stack>

      {mode === "co-edit" && onAdd && (
        <Box mt={comments.length > 0 ? 1 : 0}>
          {adding ? (
            <Stack spacing={1}>
              <TextField
                size="small"
                multiline
                minRows={2}
                maxRows={6}
                placeholder="Leave a comment for the officer…"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                autoFocus
              />
              <Box display="flex" gap={1} justifyContent="flex-end">
                <Button
                  size="small"
                  onClick={() => {
                    setDraft("");
                    setAdding(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!draft.trim()}
                  onClick={submit}
                >
                  Add comment
                </Button>
              </Box>
            </Stack>
          ) : (
            <Button
              size="small"
              startIcon={<AddCommentOutlinedIcon />}
              onClick={() => setAdding(true)}
              sx={{ mt: 0.5 }}
            >
              Add comment
            </Button>
          )}
        </Box>
      )}
    </Paper>
  );
}
